import { memo, useState, useCallback, useEffect, useMemo } from "react";
import { useI18n } from "@/i18n";
import { useRole } from "@/hooks/useRole";
import { updateLocationStatus, isLocationError } from "@/infrastructure/location/locationService";
import { listTypeUnits } from "@/infrastructure/type-unit/typeUnitService";
import type { Location, LocationErrorCode, TypeUnit, InventoryStock } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";

export const LocationDrawer = function LocationDrawer({
  locations,
  onClose,
  onStatusChanged,
}: {
  locations: Location[];
  onClose: () => void;
  onStatusChanged: () => void;
}) {
  const { t } = useI18n();
  const [units, setUnits] = useState<TypeUnit[]>([]);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  useEffect(() => { listTypeUnits().then(setUnits).catch(() => {}); }, []);

  const primary = locations[0];
  if (!primary) return null;

  const unitName = units.find(u => u.id === primary.typeUnitId);
  const capacityLabel = primary.capacity
    ? `${primary.capacity} ${unitName?.abbreviation ?? ""}`
    : "—";

  const handleToggle = useCallback(async (loc: Location) => {
    setTogglingId(loc.id);
    const newStatus = loc.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await updateLocationStatus({ id: loc.id, status: newStatus });
      setToast({ message: t.location.successStatusChanged, type: "success" });
      onStatusChanged();
    } catch (err) {
      if (isLocationError(err)) {
        const code = err.message as LocationErrorCode;
        const msg = code === "HAS_INVENTORY" ? t.location.errorHasInventory : t.location.errorForbidden;
        setToast({ message: msg, type: "error" });
      }
    }
    setTogglingId(null);
    setTimeout(() => setToast(null), 3000);
  }, [onStatusChanged, t.location]);

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 250 }}
      />
      <aside
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0, width: "500px",
          background: "#fff", zIndex: 260, boxShadow: "-8px 0 32px rgba(0,0,0,0.1)",
          display: "flex", flexDirection: "column", animation: "slideInRight 0.25s ease",
          overflowY: "auto",
        }}
      >
        <div style={{ padding: "24px 24px 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>
              {primary.locationCode.replace(/-D\d+$/, "")}
            </h2>
            <StatusBadge status={primary.status} />
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: "none", border: "none", cursor: "pointer", color: "#aaa", padding: "4px", display: "flex" }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px", flex: 1 }}>
          <CapacitySummary locations={locations} unitAbbr={unitName?.abbreviation ?? ""} />

          {locations.length > 1 && (
            <StockByDepthSection locationId={primary.id} locations={locations} units={units} unitAbbr={unitName?.abbreviation ?? ""} onToggleStatus={handleToggle} togglingId={togglingId} />
          )}

          {locations.length === 1 && (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.bold, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  Actions
                </span>
                <button
                  onClick={() => handleToggle(primary)}
                  disabled={togglingId === primary.id}
                  style={{
                    fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold,
                    color: "#fff", background: primary.status === "ACTIVE" ? colors.error : "#16a34a",
                    border: "none", borderRadius: "10px", padding: "12px 20px", cursor: "pointer",
                    opacity: togglingId === primary.id ? 0.5 : 1, transition: `all ${transitions.fast}`, width: "100%",
                  }}
                >
                  {primary.status === "ACTIVE" ? t.isle.deactivate : t.isle.activate}
                </button>
              </div>
              <StockByDepthSection locationId={primary.id} locations={locations} units={units} unitAbbr={unitName?.abbreviation ?? ""} onToggleStatus={handleToggle} togglingId={togglingId} />
            </>
          )}
        </div>

        {toast && (
          <div style={{ position: "absolute", bottom: "16px", left: "16px", right: "16px", background: toast.type === "success" ? "#111" : colors.error, color: "#fff", fontFamily: typography.fontFamily, fontSize: typography.size.xs, padding: "10px 16px", borderRadius: "8px", textAlign: "center" }}>
            {toast.message}
          </div>
        )}
      </aside>
    </>
  );
};
const DepthRow = memo(function DepthRow({ loc, index, unitAbbr, toggling, onToggle }: { loc: Location; index: number; unitAbbr: string; toggling: boolean; onToggle: () => void }) {
  const { canEditWarehouse } = useRole();
  const { t } = useI18n();
  const isActive = loc.status === "ACTIVE";
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderRadius: "10px", border: "1px solid #f0f0f0", background: isActive ? "#fff" : "#f5f5f5" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#111" }}>D{index} ({t.location.depthLabel})</span>
        <StatusBadge status={loc.status} />
        <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888" }}>
          {loc.capacity ? `${loc.capacity} ${unitAbbr}` : "—"}
        </span>
      </div>
      {canEditWarehouse && (
        <button
          onClick={onToggle}
          disabled={toggling}
          style={{
            fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold,
            color: isActive ? colors.error : "#16a34a", background: "transparent",
            border: `1px solid ${isActive ? "rgba(239,68,68,0.3)" : "rgba(34,197,94,0.3)"}`,
            borderRadius: "6px", padding: "5px 10px", cursor: "pointer",
            opacity: toggling ? 0.5 : 1, transition: `all ${transitions.fast}`,
          }}
        >
          {isActive ? t.location.inactivate : t.location.activate}
        </button>
      )}
    </div>
  );
});

function StatusBadge({ status }: { status: string }) {
  const { t } = useI18n();
  const isActive = status === "ACTIVE";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "4px",
      background: isActive ? "rgba(34,197,94,0.1)" : "rgba(156,163,175,0.15)",
      color: isActive ? "#16a34a" : "#6b7280",
      fontFamily: typography.fontFamily, fontSize: "10px", fontWeight: typography.weight.semibold,
      padding: "3px 8px", borderRadius: "9999px",
    }}>
      <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: isActive ? "#16a34a" : "#6b7280" }} />
      {isActive ? t.location.statusActive : t.location.statusInactive}
    </span>
  );
}

function CapacitySummary({ locations, unitAbbr }: { locations: Location[]; unitAbbr: string }) {
  const { t } = useI18n();
  const primary = locations[0]!;
  const depthCount = locations.length;
  const capacityPerLayer = primary.capacity ?? 0;
  const totalCapacity = capacityPerLayer * depthCount;

  const totalStock = locations.reduce((sum, loc) => sum + (loc.currentStock ?? 0), 0);
  const totalAvailable = totalCapacity - totalStock;
  const usagePct = totalCapacity > 0 ? Math.min(100, Math.round((totalStock / totalCapacity) * 100)) : 0;

  const barColor = usagePct >= 100 ? "#dc2626" : usagePct >= 70 ? "#f59e0b" : "#16a34a";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
        <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#999" }}>
          {primary.section} · {t.location.levelLabel ?? "Level"} {primary.level}, {t.location.positionLabel ?? "Pos"} {primary.position}
        </span>
      </div>

      {totalCapacity > 0 && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.semibold, color: "#444" }}>
                {totalStock.toFixed(2)} / {totalCapacity.toFixed(2)} {unitAbbr}
              </span>
              <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.bold, color: barColor }}>
                {usagePct}%
              </span>
            </div>
            <div style={{ height: "6px", borderRadius: "3px", background: "#f0f0f0", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${usagePct}%`, borderRadius: "3px", background: barColor, transition: "width 0.3s ease" }} />
            </div>
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <MiniInfo label={t.location.capacityPerLayer ?? "Per layer"} value={`${capacityPerLayer} ${unitAbbr}`} />
            <MiniInfo label={t.location.layers ?? "Layers"} value={String(depthCount)} />
            <MiniInfo label={t.location.availableLabel ?? "Available"} value={`${totalAvailable.toFixed(2)} ${unitAbbr}`} color={totalAvailable > 0 ? "#16a34a" : "#dc2626"} />
          </div>
        </>
      )}
    </div>
  );
}

function MiniInfo({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
      <span style={{ fontFamily: typography.fontFamily, fontSize: "9px", color: "#bbb", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
      <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.bold, color: color ?? "#111" }}>{value}</span>
    </div>
  );
}

function StockByDepthSection({ locationId, locations, units, unitAbbr, onToggleStatus, togglingId }: { locationId: string; locations: Location[]; units: TypeUnit[]; unitAbbr: string; onToggleStatus: (loc: Location) => void; togglingId: string | null }) {
  const { canEditWarehouse } = useRole();
  const { t } = useI18n();
  const [stocks, setStocks] = useState<InventoryStock[]>([]);
  const [products, setProducts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [expandedDepths, setExpandedDepths] = useState<Record<string, boolean>>({});
  const [selectedLayerIndex, setSelectedLayerIndex] = useState<number | null>(null);
  const [layerPage, setLayerPage] = useState(1);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { listInventoryStock } = await import("@/infrastructure/inventory/inventoryStockService");
        const { listProducts } = await import("@/infrastructure/product/productService");
        const res = await listInventoryStock({ locationId, limit: 200 });
        setStocks(res.items);
        if (res.items.length > 0) {
          const prodRes = await listProducts({ limit: 500 });
          const map: Record<string, string> = {};
          prodRes.items.forEach(p => { map[p.id] = p.name; });
          setProducts(map);
        }
      } catch {}
      setLoading(false);
    })();
  }, [locationId]);

  // Group stocks by depth extracted from locationCode
  const depthGroups = useMemo(() => {
    const groups: Record<string, InventoryStock[]> = {};
    for (const stock of stocks) {
      const code = stock.locationCode ?? "";
      const match = code.match(/-D(\d+)$/);
      const depth = match ? `D${match[1]}` : "D1";
      if (!groups[depth]) groups[depth] = [];
      groups[depth].push(stock);
    }
    // Sort by depth number
    const sorted = Object.entries(groups).sort(([a], [b]) => {
      const numA = parseInt(a.replace("D", ""));
      const numB = parseInt(b.replace("D", ""));
      return numA - numB;
    });
    return sorted;
  }, [stocks]);

  const totalDepths = locations.length > 1 ? locations.length : depthGroups.length;
  const occupiedDepths = depthGroups.filter(([, items]) => items.some(s => s.quantityOnHand > 0)).length;
  const totalOnHand = stocks.reduce((sum, s) => sum + s.quantityOnHand, 0);

  // Auto-expand if <=3 depths
  useEffect(() => {
    if (depthGroups.length > 0 && Object.keys(expandedDepths).length === 0) {
      const initial: Record<string, boolean> = {};
      depthGroups.forEach(([depth], i) => {
        initial[depth] = depthGroups.length <= 3;
      });
      setExpandedDepths(initial);
    }
  }, [depthGroups.length]);

  const [depthPage, setDepthPage] = useState(1);
  const DEPTH_PAGE_SIZE = 3;
  const depthTotalPages = Math.max(1, Math.ceil(depthGroups.length / DEPTH_PAGE_SIZE));
  const depthStart = (depthPage - 1) * DEPTH_PAGE_SIZE;
  const visibleDepthGroups = depthGroups.slice(depthStart, depthStart + DEPTH_PAGE_SIZE);

  const toggleDepth = useCallback((depth: string) => {
    setExpandedDepths(prev => ({ ...prev, [depth]: !prev[depth] }));
  }, []);

  if (loading) return <div style={{ padding: "12px", textAlign: "center" }}><span style={{ width: "14px", height: "14px", border: `2px solid ${colors.primary}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /></div>;

  // Build unified layer list: all locations with their stock data
  const PAGE_SIZE = 8;
  const allLayers = locations.map((loc, i) => {
    const depthKey = `D${i + 1}`;
    const layerStocks = stocks.filter(s => {
      const match = (s.locationCode ?? "").match(/-D(\d+)$/);
      const num = match ? parseInt(match[1] ?? "1") : 1;
      return num === i + 1;
    });
    const onHand = layerStocks.reduce((sum, s) => sum + s.quantityOnHand, 0);
    const available = layerStocks.reduce((sum, s) => sum + s.quantityAvailable, 0);
    return { loc, depthKey, index: i, layerStocks, onHand, available };
  });

  const totalPages = Math.ceil(allLayers.length / PAGE_SIZE);
  const pageStart = (layerPage - 1) * PAGE_SIZE;
  const visibleItems = allLayers.slice(pageStart, pageStart + PAGE_SIZE);

  const toggleLayer = (idx: number) => {
    setSelectedLayerIndex(prev => prev === idx ? null : idx);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {/* Summary */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.bold, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.08em" }}>{t.location.stockLabel}</span>
        <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#666" }}>
          {stocks.length > 0 ? (
            <><strong>{totalOnHand}</strong> {unitAbbr} · {occupiedDepths}/{totalDepths} {t.location.layers}</>
          ) : (
            <>0 {unitAbbr} · 0/{totalDepths} {t.location.layers}</>
          )}
        </span>
      </div>

      {/* Accordion layers */}
      <div style={{ display: "flex", flexDirection: "column" }}>
        {visibleItems.map(({ loc, depthKey, index: idx, layerStocks, onHand, available }) => {
          const isExpanded = selectedLayerIndex === idx;
          const isActive = loc.status === "ACTIVE";
          const capacity = loc.capacity ?? 0;
          const pct = capacity > 0 ? Math.min(100, Math.round((onHand / capacity) * 100)) : 0;
          const dotColor = !isActive ? "#9ca3af" : onHand === 0 ? "#16a34a" : capacity > 0 && onHand >= capacity ? "#ef4444" : "#f59e0b";

          return (
            <div key={loc.id}>
              {/* Row header */}
              <button
                onClick={() => toggleLayer(idx)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: "10px",
                  padding: "12px 14px", background: "#fff", border: "none",
                  borderRadius: "12px", cursor: "pointer",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
                  transition: `all ${transitions.fast}`,
                  marginBottom: "6px",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Background fill showing capacity usage */}
                {capacity > 0 && pct > 0 && (
                  <div style={{
                    position: "absolute", top: 0, left: 0, bottom: 0,
                    width: `${pct}%`,
                    background: `rgba(${dotColor === "#ef4444" ? "239,68,68" : dotColor === "#f59e0b" ? "245,158,11" : "16,185,129"}, 0.06)`,
                    borderRadius: "12px",
                    transition: "width 0.4s ease",
                    pointerEvents: "none",
                  }} />
                )}

                {/* +/- icon */}
                <span style={{
                  width: "22px", height: "22px", borderRadius: "50%",
                  background: isExpanded ? "#111" : "#f0f0f0",
                  color: isExpanded ? "#fff" : "#888",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "13px", fontWeight: 700, flexShrink: 0,
                  transition: `all ${transitions.fast}`,
                  position: "relative",
                }}>
                  {isExpanded ? "−" : "+"}
                </span>

                {/* Dot + label */}
                <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "2px", textAlign: "left", position: "relative" }}>
                  <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#111" }}>
                    D{idx + 1} <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.regular, color: "#999" }}>({t.location.depthLabel})</span>
                  </span>
                </div>

                {/* Status badge */}
                <StatusBadge status={loc.status} />

                {/* Stock amount - always visible */}
                <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: onHand > 0 ? "#666" : "#ccc", background: "#f5f5f5", padding: "2px 8px", borderRadius: "6px" }}>
                  {onHand > 0 ? `${onHand} ${unitAbbr}` : t.location.depthEmpty}
                </span>
              </button>

              {/* Expanded content */}
              {isExpanded && (
                <div style={{ padding: "0 4px 14px 36px", display: "flex", flexDirection: "column", gap: "10px", animation: "accordionOpen 0.2s ease" }}>
                  {/* Capacity bar */}
                  {capacity > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#666" }}>
                          {onHand} / {capacity} {unitAbbr}
                        </span>
                        <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.bold, color: pct >= 100 ? "#dc2626" : pct >= 70 ? "#f59e0b" : "#16a34a" }}>
                          {pct}%
                        </span>
                      </div>
                      <div style={{ height: "4px", borderRadius: "2px", background: "#f0f0f0", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, borderRadius: "2px", background: pct >= 100 ? "#dc2626" : pct >= 70 ? "#f59e0b" : "#16a34a", transition: "width 0.3s ease" }} />
                      </div>
                    </div>
                  )}

                  {/* Stats row */}
                  <div style={{ display: "flex", gap: "16px" }}>
                    <MiniStat label={t.location.onHand} value={`${onHand} ${unitAbbr}`} />
                    <MiniStat label={t.location.availableLabel} value={`${capacity > 0 ? capacity - onHand : 0} ${unitAbbr}`} color={capacity - onHand > 0 ? "#16a34a" : "#dc2626"} />
                    <MiniStat label={t.location.capacityPerLayer} value={`${capacity} ${unitAbbr}`} />
                  </div>

                  {/* Products if any */}
                  {layerStocks.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "4px" }}>
                      {layerStocks.slice(0, 3).map(stock => {
                        const unitA = units.find(u => u.id === stock.typeUnitId)?.abbreviation ?? "";
                        const productName = products[stock.productId] ?? stock.productId.slice(0, 8);
                        return (
                          <div key={stock.id} style={{ background: "#f8f8f8", borderRadius: "8px", padding: "8px 10px", display: "flex", flexDirection: "column", gap: "2px" }}>
                            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.semibold, color: "#111" }}>{productName}</span>
                            <span style={{ fontFamily: typography.fontFamily, fontSize: "10px", color: "#888" }}>
                              {stock.quantityOnHand} {unitA} · {t.location.available}: {stock.quantityAvailable} {unitA}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Toggle status button */}
                  {canEditWarehouse && (
                    <button
                      onClick={() => onToggleStatus(loc)}
                      disabled={togglingId === loc.id}
                      style={{
                        fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold,
                        color: isActive ? colors.error : "#16a34a", background: "transparent",
                        border: `1px solid ${isActive ? "rgba(239,68,68,0.25)" : "rgba(34,197,94,0.25)"}`,
                        borderRadius: "7px", padding: "7px 12px", cursor: "pointer", width: "fit-content",
                        opacity: togglingId === loc.id ? 0.5 : 1, transition: `all ${transitions.fast}`,
                      }}
                    >
                      {isActive ? t.location.inactivate : t.location.activate}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", paddingTop: "4px" }}>
          <button onClick={() => setLayerPage(p => Math.max(1, p - 1))} disabled={layerPage === 1} style={{ width: "24px", height: "24px", borderRadius: "6px", border: "1px solid #eee", background: "#fff", color: layerPage === 1 ? "#ddd" : "#555", fontSize: "12px", cursor: layerPage === 1 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: layerPage === 1 ? 0.5 : 1 }}>‹</button>
          <span style={{ fontFamily: typography.fontFamily, fontSize: "11px", color: "#888" }}>{layerPage}/{totalPages}</span>
          <button onClick={() => setLayerPage(p => Math.min(totalPages, p + 1))} disabled={layerPage === totalPages} style={{ width: "24px", height: "24px", borderRadius: "6px", border: "1px solid #eee", background: "#fff", color: layerPage === totalPages ? "#ddd" : "#555", fontSize: "12px", cursor: layerPage === totalPages ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: layerPage === totalPages ? 0.5 : 1 }}>›</button>
        </div>
      )}

      <style>{`
        @keyframes accordionOpen {
          from { opacity: 0; max-height: 0; transform: translateY(-4px); }
          to { opacity: 1; max-height: 500px; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function DepthProductList({ items, units, products }: { items: InventoryStock[]; units: TypeUnit[]; products: Record<string, string> }) {
  const { t } = useI18n();
  const PAGE_SIZE = 3;
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const pageItems = items.slice(start, start + PAGE_SIZE);

  return (
    <div style={{ padding: "8px 14px 14px", display: "flex", flexDirection: "column", gap: "8px", borderTop: "1px solid #f5f5f5" }}>
      {pageItems.map(stock => {
        const unitA = units.find(u => u.id === stock.typeUnitId)?.abbreviation ?? "";
        const productName = products[stock.productId] ?? stock.productId.slice(0, 8);
        return (
          <div key={stock.id} style={{ background: "#f8f8f8", borderRadius: "8px", padding: "10px 12px", display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.semibold, color: "#111" }}>{productName}</span>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "6px" }}>
              <MiniStat label={t.location.onHand} value={`${stock.quantityOnHand} ${unitA}`} />
              <MiniStat label={t.location.reserved} value={`${stock.quantityReserved} ${unitA}`} />
              <MiniStat label={t.location.available} value={`${stock.quantityAvailable} ${unitA}`} color={stock.quantityAvailable > 0 ? "#16a34a" : "#dc2626"} />
            </div>
            {stock.lastMovementAt && (
              <span style={{ fontFamily: typography.fontFamily, fontSize: "9px", color: "#bbb" }}>
                {t.location.lastMovement}: {new Date(stock.lastMovementAt).toLocaleDateString()}
              </span>
            )}
          </div>
        );
      })}
      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", paddingTop: "4px" }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{ width: "26px", height: "26px", borderRadius: "6px", border: "1px solid #e8e8e8", background: "#fff", color: page === 1 ? "#ddd" : "#555", fontSize: "14px", cursor: page === 1 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: page === 1 ? 0.5 : 1 }}
          >
            ‹
          </button>
          <span style={{ fontFamily: typography.fontFamily, fontSize: "11px", color: "#888" }}>
            {page}/{totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{ width: "26px", height: "26px", borderRadius: "6px", border: "1px solid #e8e8e8", background: "#fff", color: page === totalPages ? "#ddd" : "#555", fontSize: "14px", cursor: page === totalPages ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: page === totalPages ? 0.5 : 1 }}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
      <span style={{ fontFamily: typography.fontFamily, fontSize: "9px", color: "#bbb", textTransform: "uppercase" }}>{label}</span>
      <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.bold, color: color ?? "#111" }}>{value}</span>
    </div>
  );
}

function LayerDetailPopup({ location, index, unitAbbr, onClose, onToggleStatus, togglingId }: { location: Location; index: number; unitAbbr: string; onClose: () => void; onToggleStatus: (loc: Location) => void; togglingId: string | null }) {
  const { t } = useI18n();
  const { canEditWarehouse } = useRole();
  const isActive = location.status === "ACTIVE";
  const stock = location.currentStock ?? 0;
  const capacity = location.capacity ?? 0;
  const available = capacity - stock;
  const pct = capacity > 0 ? Math.min(100, Math.round((stock / capacity) * 100)) : 0;
  const barColor = pct >= 100 ? "#dc2626" : pct >= 70 ? "#f59e0b" : "#16a34a";

  return (
    <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: "14px", padding: "16px", boxShadow: "0 4px 16px rgba(0,0,0,0.08)", animation: "layerPopIn 0.2s ease", display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#111" }}>
            D{index + 1}
          </span>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            background: isActive ? "rgba(34,197,94,0.1)" : "rgba(156,163,175,0.15)",
            color: isActive ? "#16a34a" : "#6b7280",
            fontFamily: typography.fontFamily, fontSize: "10px", fontWeight: typography.weight.semibold,
            padding: "3px 8px", borderRadius: "9999px",
          }}>
            <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: isActive ? "#16a34a" : "#6b7280" }} />
            {isActive ? t.location.statusActive : t.location.statusInactive}
          </span>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#ccc", padding: "2px", display: "flex" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </div>

      {/* Capacity bar */}
      {capacity > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#666" }}>
              {stock} / {capacity} {unitAbbr}
            </span>
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.bold, color: barColor }}>
              {pct}%
            </span>
          </div>
          <div style={{ height: "5px", borderRadius: "3px", background: "#f0f0f0", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${pct}%`, borderRadius: "3px", background: barColor, transition: `width ${transitions.base}` }} />
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: "flex", gap: "12px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "2px", flex: 1 }}>
          <span style={{ fontFamily: typography.fontFamily, fontSize: "9px", color: "#bbb", textTransform: "uppercase" }}>{t.location.onHand}</span>
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.bold, color: "#111" }}>{stock} {unitAbbr}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "2px", flex: 1 }}>
          <span style={{ fontFamily: typography.fontFamily, fontSize: "9px", color: "#bbb", textTransform: "uppercase" }}>{t.location.availableLabel}</span>
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.bold, color: available > 0 ? "#16a34a" : "#dc2626" }}>{available > 0 ? available : 0} {unitAbbr}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "2px", flex: 1 }}>
          <span style={{ fontFamily: typography.fontFamily, fontSize: "9px", color: "#bbb", textTransform: "uppercase" }}>{t.location.capacityPerLayer}</span>
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.bold, color: "#111" }}>{capacity} {unitAbbr}</span>
        </div>
      </div>

      {/* Action */}
      {canEditWarehouse && (
        <button
          onClick={() => onToggleStatus(location)}
          disabled={togglingId === location.id}
          style={{
            fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold,
            color: isActive ? colors.error : "#16a34a", background: "transparent",
            border: `1px solid ${isActive ? "rgba(239,68,68,0.25)" : "rgba(34,197,94,0.25)"}`,
            borderRadius: "8px", padding: "8px 14px", cursor: "pointer", width: "100%",
            opacity: togglingId === location.id ? 0.5 : 1, transition: `all ${transitions.fast}`,
          }}
        >
          {isActive ? t.location.inactivate : t.location.activate}
        </button>
      )}

      <style>{`
        @keyframes layerPopIn {
          from { opacity: 0; transform: translateY(6px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
